"use client";

import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
// import { formatCurrency } from "../../lib/utils";

interface Application {
  id: string;
  studentId: string;
  resume: string;
  coverLetter: string;
  videoPitch: string;
  status: number; // 1=Applied, 2=Shortlisted, 3=Interviewed, 4=Offered
  jobId: string;
  jobResponse: {
    responseMessage: string;
    offerLetter: string;
    expiryDate: string;
    offerStatus: number; // 1=Pending, 2=Accepted, 3=Rejected, 4=Question
    createdOn: string;
  };
  createdOn: string;
  updatedon: string;
}

interface JobApplicationsProps {
  applications: Application[];
}

export function JobApplications({ applications }: JobApplicationsProps) {
  // Map ApplicationStatus enum values to text
  const getApplicationStatusText = (status: number) => {
    switch (status) {
      case 1:
        return "Applied";
      case 2:
        return "Shortlisted";
      case 3:
        return "Interviewed";
      case 4:
        return "Offered";
      default:
        return "Unknown";
    }
  };

  // Map OfferStatus enum values to text
  const getOfferStatusText = (offerStatus: number) => {
    switch (offerStatus) {
      case 1:
        return "Pending";
      case 2:
        return "Accepted";
      case 3:
        return "Rejected";
      case 4:
        return "Question";
      default:
        return "Unknown";
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: // Applied
        return "bg-blue-100 text-blue-800";
      case 2: // Shortlisted
        return "bg-yellow-100 text-yellow-800";
      case 3: // Interviewed
        return "bg-purple-100 text-purple-800";
      case 4: // Offered
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOfferStatusColor = (offerStatus: number) => {
    switch (offerStatus) {
      case 1: // Pending
        return "bg-yellow-100 text-yellow-800";
      case 2: // Accepted
        return "bg-green-100 text-green-800";
      case 3: // Rejected
        return "bg-red-100 text-red-800";
      case 4: // Question
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">
          Applications ({applications.length})
        </h2>
      </div>

      {applications.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 text-lg mb-2">
              No applications yet
            </p>
            <p className="text-slate-500">
              Applications will appear here when candidates apply for jobs
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <h3 className="text-lg font-semibold text-slate-800">
                        Student ID: {app.studentId || "N/A"}
                      </h3>
                      <Badge className={getStatusColor(app.status)}>
                        {getApplicationStatusText(app.status)}
                      </Badge>
                      {app.jobResponse && (
                        <Badge className={getOfferStatusColor(app.jobResponse.offerStatus)}>
                          Offer: {getOfferStatusText(app.jobResponse.offerStatus)}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
                      <div>
                        <p className="font-medium text-slate-700">Documents:</p>
                        <p>Resume: {app.resume ? "✅" : "❌"}</p>
                        <p>Cover Letter: {app.coverLetter ? "✅" : "❌"}</p>
                        <p>Video Pitch: {app.videoPitch ? "✅" : "❌"}</p>
                      </div>
                      
                      {app.jobResponse && (
                        <div>
                          <p className="font-medium text-slate-700">Response:</p>
                          <p className="truncate">
                            Message: {app.jobResponse.responseMessage || "None"}
                          </p>
                          <p>
                            Offer Letter: {app.jobResponse.offerLetter ? "✅" : "❌"}
                          </p>
                          <p>
                            Expires: {new Date(app.jobResponse.expiryDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      <div>
                        <p className="font-medium text-slate-700">Timeline:</p>
                        <p>Applied: {new Date(app.createdOn).toLocaleDateString()}</p>
                        {app.jobResponse && (
                          <p>Response: {new Date(app.jobResponse.createdOn).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
